import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Card, CardBody, Input, Spinner } from "reactstrap";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSlug from "rehype-slug";
import debounce from "lodash.debounce";
import i18n from "../../i18n";

const Documentation = (props) => {
  const [markdown, setMarkdown] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  document.title = `Veritas | ${props.t("documentation.title")}`;

  const debouncedSetSearch = useMemo(
    () => debounce((val) => setDebouncedSearch(val), 300),
    []
  );

  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    debouncedSetSearch(val);
  };

  const fetchDocs = useCallback(() => {
    setLoading(true);
    setError(null);
    const lang = i18n.language || "en";
    const file = lang === "pt" ? "FEATURES.pt.md" : "FEATURES.md";
    fetch(`${process.env.PUBLIC_URL}/docs/${file}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        setMarkdown(text);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  useEffect(() => {
    const handleLanguageChanged = () => fetchDocs();
    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);
  }, [fetchDocs]);

  const sections = useMemo(() => {
    if (!markdown) return [];
    const parts = markdown.split(/^(?=## )/m);
    return parts.map((part) => {
      const titleMatch = part.match(/^## (.+)/);
      return {
        title: titleMatch ? titleMatch[1] : "",
        content: part,
      };
    });
  }, [markdown]);

  const filteredSections = useMemo(() => {
    if (!debouncedSearch.trim()) return sections;
    const term = debouncedSearch.toLowerCase();
    return sections.filter(
      (s) =>
        s.title.toLowerCase().includes(term) ||
        s.content.toLowerCase().includes(term)
    );
  }, [sections, debouncedSearch]);

  const highlightText = useCallback(
    (text) => {
      if (!debouncedSearch.trim() || typeof text !== "string") return text;
      const term = debouncedSearch.trim();
      const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escaped})`, "gi");
      const parts = text.split(regex);
      if (parts.length === 1) return text;
      return parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i}>{part}</mark>
        ) : (
          part
        )
      );
    },
    [debouncedSearch]
  );

  const markdownComponents = useMemo(
    () => ({
      table: ({ children, ...props }) => (
        <div className="table-responsive">
          <table className="table table-bordered table-sm" {...props}>
            {children}
          </table>
        </div>
      ),
      p: ({ children, ...props }) => (
        <p {...props}>
          {React.Children.map(children, (child) =>
            typeof child === "string" ? highlightText(child) : child
          )}
        </p>
      ),
      li: ({ children, ...props }) => (
        <li {...props}>
          {React.Children.map(children, (child) =>
            typeof child === "string" ? highlightText(child) : child
          )}
        </li>
      ),
      td: ({ children, ...props }) => (
        <td {...props}>
          {React.Children.map(children, (child) =>
            typeof child === "string" ? highlightText(child) : child
          )}
        </td>
      ),
      th: ({ children, ...props }) => (
        <th {...props}>
          {React.Children.map(children, (child) =>
            typeof child === "string" ? highlightText(child) : child
          )}
        </th>
      ),
    }),
    [highlightText]
  );

  return (
    <div className="page-content layout-main-container">
      <Card className="container-body card-section">
        <CardBody>
          <h4 className="mb-3">{props.t("documentation.title")}</h4>
          <div className="documentation-search mb-3">
            <div className="position-relative">
              <i className="ri-search-line position-absolute top-50 translate-middle-y" style={{ left: 12 }}></i>
              <Input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={props.t("documentation.searchPlaceholder")}
                style={{ paddingLeft: 36 }}
              />
            </div>
            {debouncedSearch.trim() && (
              <small className="text-muted mt-1 d-block">
                {props.t("documentation.sectionsFound", {
                  count: filteredSections.length,
                })}
              </small>
            )}
          </div>

          {loading && (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          )}

          {error && (
            <div className="alert alert-danger">{error}</div>
          )}

          {!loading && !error && filteredSections.length === 0 && debouncedSearch.trim() && (
            <div className="text-center text-muted py-4">
              {props.t("documentation.noResults")}
            </div>
          )}

          {!loading && !error && (
            <div className="documentation-content">
              {filteredSections.map((section, idx) => (
                <ReactMarkdown
                  key={idx}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeSlug]}
                  components={markdownComponents}
                >
                  {section.content}
                </ReactMarkdown>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default withTranslation()(Documentation);
